import MainLayout from "@/components/layout/MainLayout";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Template } from "@/types";
import { AlertCircle } from "lucide-react";
import { useNotifications } from "@/contexts/NotificationContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import TemplatesHeader from "@/components/templates/TemplatesHeader";
import TemplateList from "@/components/templates/TemplateList";
import DeleteTemplateDialog from "@/components/templates/DeleteTemplateDialog";
import TemplateCardSkeleton from "@/components/templates/TemplateCardSkeleton";
import TemplatePreviewDialog from "@/components/templates/TemplatePreviewDialog";
import { useTranslation } from "react-i18next";

const Templates = () => {
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [templateToPreview, setTemplateToPreview] = useState<Template | null>(null);
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const queryClient = useQueryClient();

  // Fetch templates from Supabase
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["templates", i18n.language],
    queryFn: async () => {
      const currentLang = i18n.language.split('-')[0]; // Handle 'ru-RU' -> 'ru'
      const { data, error } = await supabase
        .from("templates")
        .select("*")
        .is("deleted_at", null)
        .order("order_code", { ascending: true, nullsFirst: false })
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching templates:", error);
        throw error;
      }

      // Filter system templates by language client-side to handle nulls safely
      const filteredData = (data || []).filter(t => !t.is_system || t.lang === currentLang);

      // Преобразуем данные из snake_case в camelCase
      return filteredData.map((template) => ({
        id: template.id,
        name: template.name,
        description: template.description,
        prompt: template.prompt,
        preview_template_result: template.preview_template_result,
        isSystem: template.is_system,
        userId: template.user_id,
        createdAt: template.created_at,
        deletedAt: template.deleted_at,
        orderCode: template.order_code,
        lang: template.lang,
      }));
    },
  });


  // Duplicate template mutation
  const duplicateTemplateMutation = useMutation({
    mutationFn: async (template: Template) => {
      const { data, error } = await supabase
        .from("templates")
        .insert({
          name: `${template.name} (копия)`,
          description: template.description,
          prompt: template.prompt,
          is_system: false,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      addNotification({
        userId: "user-123",
        type: "success",
        message: t('templates.toasts.copy_success'),
      });
    },
    onError: (error) => {
      console.error("Error duplicating template:", error);
      addNotification({
        userId: "user-123",
        type: "error",
        message: t('templates.toasts.copy_error'),
      });
    },
  });

  const handleEditTemplate = (template: Template) => {
    navigate(`/templates/edit/${template.id}`);
  };

  const handleDeleteClick = (template: Template) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!templateToDelete) return;
    // Удаление будет обработано в DeleteTemplateDialog компоненте
    setDeleteDialogOpen(false);
    setTemplateToDelete(null);
    addNotification({
      userId: "user-123",
      type: "success",
      message: t('templates.toasts.delete_success'),
    });
    queryClient.invalidateQueries({ queryKey: ["templates"] });
  };

  const handleDuplicateTemplate = (template: Template) => {
    duplicateTemplateMutation.mutate(template);
  };

  const handlePreviewClick = (template: Template) => {
    setTemplateToPreview(template);
    setPreviewDialogOpen(true);
  };

  const filteredTemplates = templates.filter((template) => {
    return template.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (isLoading) {
    return (
      <MainLayout>
        <div className="container-page">
          <div className="space-y-4">
            <div className="h-8 bg-muted rounded w-1/4 animate-pulse"></div>
            <div className="h-10 bg-muted rounded w-full animate-pulse"></div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                {Array.from({ length: 6 }, (_, i) => (
                  <TemplateCardSkeleton key={i} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container-page">
        <TemplatesHeader searchTerm={searchTerm} onSearchChange={setSearchTerm} />

        <TemplateList
          templates={filteredTemplates}
          onPreviewClick={handlePreviewClick}
        />

        {filteredTemplates.length > 0 && (
          <div className="mt-6 bg-blue-50 dark:bg-slate-800 border border-blue-100 dark:border-slate-700 rounded-lg p-4 flex items-start gap-3">
            <div className="mt-0.5 text-blue-500 dark:text-blue-400">
              <AlertCircle size={20} />
            </div>
            <div>
              <h4 className="font-medium text-blue-800 dark:text-slate-200 mb-1">{t('templates.info.title')}</h4>
              <p className="text-sm text-blue-700 dark:text-slate-300">
                {t('templates.info.desc')}
              </p>
            </div>
          </div>
        )}

        <DeleteTemplateDialog
          template={templateToDelete}
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={confirmDelete}
        />

        {templateToPreview && (
          <TemplatePreviewDialog
            template={templateToPreview}
            open={previewDialogOpen}
            onOpenChange={setPreviewDialogOpen}
          />
        )}
      </div>
    </MainLayout>
  );
};

export default Templates;
